import { alpha, createTheme, Theme } from '@mui/material';
import { CSSProperties } from 'react';
import { FontFamilyKey, FontWeightKey, getFontFamily, getWeightProfile } from './fontConfig';
import { recommendedLicenseCardStyles } from './licenseCardStyles';
import { upgradeBannerStyles } from './upgradeBannerStyles';
import { emailEditorButtonStyles, emailEditorCardStyles, emailEditorPaperStyles } from './emailTemplateEditorStyles';
import { licenceChangeStyles } from './licenceChangeStyles';
import { sharedDocumentsStyles } from './sharedDocumentsStyles';
import { evalIsRefused } from '../security/bootstrap';

// Extend MUI Theme typings to allow MuiDataGrid
declare module '@mui/material/styles' {
	interface Components {
		MuiDataGrid?: {
			defaultProps?: { disableEval?: boolean };
			styleOverrides?: {
				root?: CSSProperties | ((props: { theme: Theme }) => CSSProperties);
				columnHeaders?: CSSProperties | ((props: { theme: Theme }) => CSSProperties);
				row?: CSSProperties | ((props: { theme: Theme }) => CSSProperties);
				cell?: CSSProperties | ((props: { theme: Theme }) => CSSProperties);
				virtualScroller?: CSSProperties | ((props: { theme: Theme }) => CSSProperties);
			};
		};
	}
}

const createInspirusDark = (fontFamilyKey: FontFamilyKey = 'inter', fontWeightKey: FontWeightKey = 'regular') => {
	const fontFamily = getFontFamily(fontFamilyKey);
	const wp = getWeightProfile(fontWeightKey);

	return createTheme({
		palette: {
			mode: 'dark',
			primary: { main: '#5b9bff', dark: '#002868', contrastText: '#ffffff' },
			secondary: { main: '#acacac' },
			success: { main: '#6be898' },
			warning: { main: '#fcc47a' },
			error: { main: '#ff6b6b' },
			info: { main: '#a8ecf5' },
			background: {
				default: '#000d1f', // navy-based main background
				paper: '#001E45' // navy brand colour — sidebar, cards, glass base
			},
			text: {
				primary: '#ffffff',
				secondary: alpha('#ffffff', 0.7)
			}
		},
		typography: {
			fontFamily,
			h1: { fontWeight: wp.heading },
			h2: { fontWeight: wp.heading },
			h3: { fontWeight: wp.heading },
			h4: { fontWeight: wp.heading },
			h5: { fontWeight: wp.heading },
			h6: { fontWeight: wp.heading },
			subtitle1: { fontWeight: wp.body },
			subtitle2: { fontWeight: wp.body },
			body1: { fontWeight: wp.body },
			body2: { fontWeight: wp.body },
			button: { fontWeight: wp.button },
			caption: { fontWeight: wp.caption },
			overline: { fontWeight: wp.caption }
		},
		components: {
			MuiPaper: {
				styleOverrides: {
					root: ({ theme }) => ({
						/* ===== Glass styles — excluded from chat bubbles ===== */
						'&:not(.chat-bubble)': {
							backgroundImage: 'none',
							backgroundColor: alpha(theme.palette.background.paper, 0.55),
							backdropFilter: 'blur(var(--glass-blur, 12px))',
							WebkitBackdropFilter: 'blur(var(--glass-blur, 12px))',
							border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
							boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.2)}`
						},

						/* ===== Carousel dark glass variant ===== */
						'&.glass-carousel-card': {
							backgroundColor: alpha(theme.palette.background.paper, 0.75),
							borderRadius: 20,
							padding: 10,
							overflow: 'visible',
							transition: 'all 0.3s ease',

							'&:hover': {
								transform: 'translateY(-4px)',
								boxShadow: `
            0 0 12px ${alpha(theme.palette.primary.main, 0.7)},
            0 0 28px ${alpha(theme.palette.primary.main, 0.5)},
            0 0 52px ${alpha(theme.palette.primary.main, 0.4)}
          `
							}
						},

						...upgradeBannerStyles(theme),
						...emailEditorPaperStyles(theme),
						...licenceChangeStyles(theme),
						...sharedDocumentsStyles(theme)
					})
				}
			},
			MuiDrawer: {
				styleOverrides: {
					paper: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.background.paper, 0.85),
						backdropFilter: 'blur(var(--glass-blur, 20px))',
						WebkitBackdropFilter: 'blur(var(--glass-blur, 20px))'
					})
				}
			},
			MuiMenu: {
				styleOverrides: {
					paper: ({ theme }) => ({
						backgroundColor: theme.palette.background.paper,
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
						backgroundImage: 'none'
					})
				}
			},
			MuiPopover: {
				styleOverrides: {
					paper: ({ theme }) => ({
						backgroundColor: theme.palette.background.paper,
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
						backgroundImage: 'none'
					})
				}
			},
			MuiCard: {
				styleOverrides: {
					root: ({ theme }) => ({
						borderRadius: 20,
						background: alpha(theme.palette.background.paper, 0.2),
						backdropFilter: 'blur(var(--glass-blur, 10px))',
						WebkitBackdropFilter: 'blur(var(--glass-blur, 10px))',
						border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
						boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
						transition: 'all 0.3s ease',
						'&:hover': {
							boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.25)}`,
							transform: 'translateY(-2px)'
						},
						...recommendedLicenseCardStyles(theme),
						...emailEditorCardStyles(theme)
					})
				}
			},
			MuiAlert: {
				styleOverrides: { root: { borderRadius: 15 } }
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						border: 'none',
						borderRadius: '0',
						padding: '2px',
						paddingInline: '20px',
						color: '#fff',
						fontWeight: 400,
						'&.Mui-selected': {
							backgroundColor: 'unset',
							borderBottom: '2px solid #5b9bff',
							fontWeight: 500,
							'&:hover': { backgroundColor: 'unset' }
						}
					}
				}
			},

			// Tabs: draw the active underline with CSS instead of MUI's JS-measured
			// indicator. The global `zoom` on <body> scales getBoundingClientRect twice,
			// which drifts the measured indicator left the further a tab sits from the
			// origin (e.g. the last tab's underline lands under the previous tab). A pure
			// CSS underline scales uniformly with zoom, so it always aligns.
			MuiTabs: {
				styleOverrides: {
					indicator: { display: 'none' }
				}
			},
			MuiTab: {
				styleOverrides: {
					root: ({ theme }) => ({
						'&.Mui-selected': {
							boxShadow: `inset 0 -2px 0 ${theme.palette.primary.main}`
						}
					})
				}
			},
			MuiButton: {
				styleOverrides: {
					root: ({ theme }) => ({
						// Base rounded/glass style (same as light theme)
						borderRadius: '999px',
						padding: '12px 30px',
						fontWeight: 600,
						textTransform: 'none',
						backdropFilter: 'blur(10px)',
						transition: 'all 0.3s ease',

						// Dark theme color overrides
						color: 'rgba(255,255,255,0.9)', // primary.contrastText in dark mode
						backgroundColor: 'rgba(0,40,104,0.35)', // alpha(primary.light, 0.35)
						border: '1px solid rgba(0,40,104,0.45)',
						boxShadow: '0 4px 30px rgba(0,40,104,0.25)',

						// Remove MUI dark grey overlay artifacts
						backgroundImage: 'none',
						'&::before': { display: 'none' },
						'&.Mui-focusVisible': { outline: 'none' },

						// Hover / active states
						'&:hover': {
							backgroundColor: 'rgba(0,40,104,0.45)',
							boxShadow: '0 6px 40px rgba(0,40,104,0.4)',
							transform: 'translateY(-2px)'
						},
						'&:active': {
							backgroundColor: 'rgba(0,40,104,0.4)',
							boxShadow: '0 4px 20px rgba(0,40,104,0.3)',
							transform: 'translateY(0)'
						},

						'&.Mui-disabled': {
							color: 'rgba(255,255,255,0.35)',
							backgroundColor: 'rgba(255,255,255,0.06)',
							border: '1px solid rgba(255,255,255,0.12)',
							boxShadow: 'none',
							backdropFilter: 'none',
							transform: 'none'
						},
						// Customisable-email editor buttons (PTEC-895); applied via className.
						...emailEditorButtonStyles(theme),
						// "Skip MFA for now" call-to-action. A softer, squarer outlined
						// button with a gentle breathing glow to draw the eye without
						// shouting. Applied via className so the styling lives in the theme.
						'&.mfa-skip-button': {
							position: 'relative',
							textTransform: 'none',
							fontWeight: 600,
							letterSpacing: '0.2px',
							padding: '8px 28px',
							borderRadius: 8,
							borderWidth: '1.5px',
							transition: 'border-color 160ms ease, box-shadow 160ms ease, color 160ms ease',
							'@keyframes mfaSkipGlow': {
								'0%, 100%': {
									boxShadow: '0 0 0 0 rgba(91,155,255,0)'
								},
								'50%': {
									boxShadow: '0 4px 18px -2px rgba(91,155,255,0.45)'
								}
							},
							animation: 'mfaSkipGlow 2.4s ease-in-out infinite',
							'&:hover': {
								borderWidth: '1.5px',
								boxShadow: '0 6px 22px -4px rgba(91,155,255,0.5)'
							},
							'@media (prefers-reduced-motion: reduce)': {
								animation: 'none',
								boxShadow: '0 2px 12px -2px rgba(91,155,255,0.4)'
							}
						}
					}),

					containedPrimary: {
						// No extra overrides needed; root handles everything
					},

					// Branded "secondary" — a confident blue glass button. Same liquid-glass
					// shape as every other button, just a deeper fill so it reads as branded.
					containedSecondary: {
						color: 'rgba(255,255,255,0.95)',
						backgroundColor: 'rgba(91,155,255,0.32)',
						border: '1px solid rgba(91,155,255,0.45)',
						boxShadow: '0 4px 30px rgba(0,0,0,0.3)',
						'&:hover': {
							backgroundColor: 'rgba(91,155,255,0.46)',
							boxShadow: '0 6px 40px rgba(0,0,0,0.4)',
							transform: 'translateY(-2px)'
						}
					},
					outlinedSecondary: {
						color: 'rgba(255,255,255,0.92)',
						backgroundColor: 'rgba(91,155,255,0.08)',
						border: '1px solid rgba(91,155,255,0.5)',
						'&:hover': {
							backgroundColor: 'rgba(91,155,255,0.18)',
							border: '1px solid rgba(91,155,255,0.7)'
						}
					},
					textSecondary: {
						color: 'rgba(255,255,255,0.85)',
						backgroundColor: 'transparent',
						border: 'none',
						boxShadow: 'none',
						'&:hover': { backgroundColor: 'rgba(91,155,255,0.16)' }
					}
				}
			},
			/**
			 * `disableEval` is not styling, but this is the one place every grid in
			 * the app passes through.
			 *
			 * The grid builds its filter function with `eval` when it can, for speed.
			 * Under this app's Content-Security-Policy that attempt is refused —
			 * nothing breaks, the grid falls back to a plain loop — but the browser
			 * reports the refusal, so every page carrying a grid wrote a
			 * `[CSP] script-src blocked eval` line to the console and to CloudWatch
			 * RUM. A refusal that always fires is worse than none: the runbook tells
			 * an operator to investigate those lines, and one that never means
			 * anything teaches them not to.
			 *
			 * Saying so up front skips the attempt — but only from @mui/x-data-grid
			 * 6.20.4, where the test became `disableEval || getHasEval()` and so
			 * short-circuits. Before that the probe ran when the module was imported
			 * and no prop could reach it, which is why that version is a floor for
			 * this package rather than an incidental bump.
			 *
			 * Asked of the page rather than hardcoded, so an environment carrying no
			 * policy keeps the optimisation it would otherwise lose. Read when the
			 * theme is built, so the very first load after the policy is switched on
			 * can still answer false — the same next-load property the rest of these
			 * settings have.
			 */
			MuiDataGrid: {
				defaultProps: {
					disableEval: evalIsRefused()
				},
				styleOverrides: {
					root: ({ theme }) => ({
						borderRadius: 20,
						overflow: 'hidden',
						background: alpha(theme.palette.background.paper, 0.2),
						backdropFilter: 'blur(var(--glass-blur, 10px))',
						WebkitBackdropFilter: 'blur(var(--glass-blur, 10px))',
						border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
						boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`
					}),
					virtualScroller: { borderRadius: 20 },
					columnHeaders: ({ theme }) => ({
						background: alpha(theme.palette.primary.main, 0.1),
						backdropFilter: 'blur(8px)'
					}),
					row: ({ theme }) => ({
						borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
						'&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) }
					}),
					cell: ({ theme }) => ({
						borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`
					})
				}
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						backgroundColor: alpha('#1f1f1f', 0.25),
						backdropFilter: 'blur(10px)',
						borderRadius: '16px',
						border: `1px solid ${alpha('#fff', 0.15)}`,
						boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
						'&:before': { display: 'none' }
					}
				}
			},
			MuiDialog: {
				styleOverrides: {
					paper: ({ theme }) => ({
						borderRadius: 20,
						backgroundColor: alpha(theme.palette.background.paper, 0.88),
						backdropFilter: 'blur(var(--glass-blur, 20px))',
						WebkitBackdropFilter: 'blur(var(--glass-blur, 20px))',
						border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
						boxShadow: `
                        0 24px 60px ${alpha(theme.palette.common.black, 0.6)},
                        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.15)}
                    `
					})
				}
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.background.paper, 0.35),
						backdropFilter: 'blur(var(--glass-blur, 14px))',
						WebkitBackdropFilter: 'blur(var(--glass-blur, 14px))',
						borderRadius: 12,
						transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color'], {
							duration: 200
						}),
						'& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.25) },
						'&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.45) },
						'&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 1.5 },
						'&.Mui-error fieldset': { borderColor: theme.palette.error.main },
						'&.Mui-disabled': {
							backgroundColor: alpha(theme.palette.background.paper, 0.2),
							backdropFilter: 'blur(8px)',
							opacity: 0.6
						}
					}),
					input: ({ theme }) => ({ color: theme.palette.text.primary, padding: '12px 14px' })
				}
			},
			MuiInputLabel: {
				styleOverrides: {
					root: ({ theme }) => ({
						color: alpha(theme.palette.text.primary, 0.7),
						'&.Mui-focused': { color: theme.palette.primary.main },
						'&.Mui-error': { color: theme.palette.error.main }
					})
				}
			},
			MuiFormHelperText: {
				styleOverrides: {
					root: ({ theme }) => ({
						marginLeft: 4,
						color: alpha(theme.palette.text.secondary, 0.75),
						'&.Mui-error': { color: theme.palette.error.main }
					})
				}
			},
			MuiTypography: {
				styleOverrides: {
					root: {
						color: '#ffffff',
						opacity: 0.9
					},
					h1: { color: '#ffffff', opacity: 0.9 },
					h2: { color: '#ffffff', opacity: 0.9 },
					h3: { color: '#ffffff', opacity: 0.9 },
					h4: { color: '#ffffff', opacity: 0.9 },
					h5: { color: '#ffffff', opacity: 0.9 },
					h6: { color: '#ffffff', opacity: 0.9 },
					subtitle1: { color: '#ffffff', opacity: 0.9 },
					subtitle2: { color: '#ffffff', opacity: 0.9 }
				}
			},
			MuiSvgIcon: {
				styleOverrides: {
					root: ({ ownerState }) => ({
						// ONLY apply default color if no color prop is set
						...((ownerState.color === undefined || ownerState.color === 'inherit') && {
							color: '#fff'
						})
					})
				}
			},
			MuiIconButton: {
				styleOverrides: {
					root: ({ ownerState }) => ({
						// Respect explicit colors like "success", "primary", etc
						...((ownerState.color === undefined || ownerState.color === 'inherit') && {
							color: '#fff'
						})
					})
				}
			},
			MuiBadge: {
				styleOverrides: {
					badge: () => ({
						// this targets the badge circle itself
						backgroundColor: '#fff',
						color: '#fff'
					})
				}
			},
			MuiBreadcrumbs: {
				styleOverrides: {
					root: ({ theme }) => ({
						color: theme.palette.text.secondary
					})
				}
			},
			MuiAutocomplete: {
				styleOverrides: {
					paper: ({ theme }) => ({
						backgroundColor: theme.palette.background.paper,
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',

						borderRadius: 14,
						border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
						boxShadow: `0 12px 36px ${alpha(theme.palette.common.black, 0.3)}`,

						/* Prevent Autocomplete from flattening Paper */
						backgroundImage: 'none'
					}),

					listbox: ({ theme }) => ({
						padding: 8,

						'& .MuiAutocomplete-option': {
							borderRadius: 8,
							marginBottom: 4,
							color: theme.palette.text.primary,

							'&[aria-selected="true"]': {
								backgroundColor: alpha(theme.palette.primary.main, 0.25)
							},

							'&.Mui-focused': {
								backgroundColor: alpha(theme.palette.primary.main, 0.18)
							},

							'&:hover': {
								backgroundColor: alpha(theme.palette.primary.main, 0.2)
							}
						}
					})
				}
			},
			MuiCssBaseline: {
				styleOverrides: {
					':root': {
						'--mui-palette-text-primary': '#ffffff',
						'--mui-palette-text-secondary': '#ffffff'
					},
					'[data-mui-color-scheme="dark"]': {
						'--mui-palette-text-primary': '#ffffff',
						'--mui-palette-text-secondary': '#ffffff'
					},
					html: {
						WebkitTextSizeAdjust: '100%',
						WebkitFontSmoothing: 'antialiased'
					},
					body: {
						color: '#ffffff',
						WebkitTextFillColor: '#ffffff',
						forcedColorAdjust: 'none'
					},
					'*': {
						forcedColorAdjust: 'none'
					}
				}
			}
		}
	});
};

export default createInspirusDark;
export { createInspirusDark };
